import React, { FormEvent, useEffect } from 'react'
import { useFetcher, useLoaderData, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { setLocale } from 'yup';

// components
import TextInput from '../components/TextInput/TextInput';

// helper
import { getReservation, updateReservationStep2 } from '../helpers/localStorage';

setLocale({
  mixed: {
    required: 'Valore richiesto',
    default: 'Valore non valido',
  },
  string: {
    email: 'Deve essere un indirizzo email valido',
    min: 'Deve essere di almeno ${min} caratteri',
    max: 'Deve essere di massimo ${max} caratteri',
  },
  number: {
    min: 'Deve essere maggiore di ${min}',
    max: 'Deve essere minore di ${min}',
  },
});

export function step2Loader() {
  const reservation = getReservation();
  return { reservation };
}

export async function step2Action({ request }: { request: Request }) {
  const data = await request.formData();
  const { _action, ...values } = Object.fromEntries(data);

  if (_action === "step2") {
    try {
      updateReservationStep2({
        firstName: values.firstName as string,
        lastName: values.lastName as string,
        phone: values.phone as string,
        email: values.email as string,
        emailCheck: values.emailCheck as string,
      });
      
      // return toast.success('Dati salvati correttamente', {
      //   autoClose: 1000,
      //   pauseOnFocusLoss: false,
      //   theme: "colored",
      //   position: "top-center",
      //   onClose: () => window.location.href = "/step3",
      // });

      
      // const baseUrl = process.env.REACT_APP_BASEURL;
      // return window.location.href = baseUrl + "/step3";

      return true;

    } catch (err) {
      console.error(err);
      throw new Error("C'è stato un problema con la tua prenotazione. Riprova più tardi.");
    }
  }
}

interface Props { }

const Step2Page: React.FC<Props> = () => {
  const fetcher = useFetcher();
  const navigate = useNavigate();

  const { reservation } = useLoaderData() as any;

  // Those fields are required to handle the form input values changes
  const [firstName, setFirstName] = React.useState<string>(reservation.firstName || '');
  const [lastName, setLastName] = React.useState<string>(reservation.lastName || '');
  const [phone, setPhone] = React.useState<string>(reservation.phone || '');
  const [email, setEmail] = React.useState<string>(reservation.email || '');
  const [emailCheck, setEmailCheck] = React.useState<string>(reservation.emailCheck || '');

  useEffect(() => {
    if (fetcher.data && fetcher.data === true) {
      navigate('/step3');
    }
  }, [fetcher.data, navigate]);

  const step2Schema = yup.object({
    firstName: yup.string().required().min(3).max(16).matches(/^[a-zA-ZÀ-ÿ\s]+$/, 'Solo lettere ammesse'),
    lastName: yup.string().required().min(3).max(16).matches(/^[a-zA-ZÀ-ÿ\s]+$/, 'Solo lettere ammesse'),
    phone: yup.string().required().min(7, 'Deve essere di almeno ${min} numeri').max(14, 'Deve essere al massimo di ${max} numeri').matches(/^((\+|00)[1-9]{2,3})?[0-9]{7,14}$/, 'Numero di telefono non valido'),
    email: yup.string().required().email().matches(/^[a-z0-9._%+\\-]+@[a-z0-9\\-]+\.[a-z]{2,4}$/, 'Deve essere un indirizzo email valido'),
    emailCheck: yup.string().required().email().matches(/^[a-z0-9._%+\\-]+@[a-z0-9\\-]+\.[a-z]{2,4}$/, 'Deve essere un indirizzo email valido').oneOf([email], 'Le email non corrispondono'),
  });

  const isSubmitting = fetcher.state === "submitting";

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {

    e.preventDefault();

    let formData = {
      firstName: step2Schema.fields.firstName.cast(firstName.trim()) as string,
      lastName: step2Schema.fields.lastName.cast(lastName.trim()) as string,
      phone: step2Schema.fields.phone.cast(phone.trim()) as string,
      email: step2Schema.fields.email.cast(email.trim()) as string,
      emailCheck: step2Schema.fields.emailCheck.cast(emailCheck.trim()) as string,
    };

    // Validate all fields together
    step2Schema.validate(formData, { strict: true })
      .then(() => {
        fetcher.submit(
          { _action: 'step2', ...formData },
          { method: 'post' }
        );
      })
      .catch((err: yup.ValidationError) => {
        return toast.error(err.message, { theme: "colored", position: "bottom-center" });
      });
  };

  return (
    <div className="customer-data">

      <h2>Step 2 - Dati personali</h2>

      <fetcher.Form method="post" onSubmit={onSubmit}>

        {/* <input type="hidden" name="_action" value="step2" /> */}

        <TextInput
          id='first-name'
          name='firstName'
          label='Nome *'
          className='data'
          yupSchema={step2Schema.fields.firstName as yup.StringSchema<string>}
          value={firstName}
          onChange={(e) => setFirstName(e.currentTarget.value)}
          required={true}
          />

        <TextInput
          id='last-name'
          name='lastName'
          label='Cognome *'
          className='data'
          value={lastName}
          onChange={(e) => setLastName(e.currentTarget.value)}
          yupSchema={step2Schema.fields.lastName as yup.StringSchema<string>}
          required={true}
        />

        <TextInput
          id='phone'
          name='phone'
          type='tel'
          suggestion='3281111111'
          label='Telefono *'
          className='data'
          value={phone}
          onChange={(e) => setPhone(e.currentTarget.value)}
          yupSchema={step2Schema.fields.phone as yup.StringSchema<string>}
          required={true}
        />

        <TextInput
          id='email'
          name='email'
          type='email'
          label='Email *'
          className='data'
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          yupSchema={step2Schema.fields.email as yup.StringSchema<string>}
          required={true}
        />

        <TextInput
          id='emailCheck'
          name='emailCheck'
          type='email'
          label='Conferma email *'
          className='data'
          value={emailCheck}
          onChange={(e) => setEmailCheck(e.currentTarget.value)}
          yupSchema={step2Schema.fields.emailCheck as yup.StringSchema<string>}
          required={true}
        />

        <button type="submit" className="btn btn--dark" disabled={isSubmitting}>
          {isSubmitting ? (
            <span>Salvando...</span>
          ) : (
            <>
              <span>Conferma e Continua</span>
              {/* <CurrencyEuroIcon width={20} /> */}
            </>
          )}
        </button>

      </fetcher.Form>

       {/* <pre>{JSON.stringify({firstName, lastName, phone, email, emailCheck}, null, 2)}</pre> */}

    </div>
  )
}
export default Step2Page;